/**
 * Centralized Environment Configuration
 * 
 * This file provides a single source of truth for all environment variables
 * used by the frontend application. It separates client-side environment
 * variables from server-side ones and provides fallback values.
 */

// Client-side environment variables (prefixed with VITE_)
import { warn } from '../utils/console';

export const config = {
  // API Configuration - Always use relative URLs (nginx handles routing)
  get API_BASE_URL() {
    return '/api';
  },
  get ADMIN_BASE_URL() {
    return '/api/admin';
  },
  get FILE_BASE_URL() {
    return '/api/file';
  },
  get MEDIA_BASE_URL() {
    return '/api/media';
  },
  get PYTHON_BASE_URL() {
    return '/api/python';
  },
  get STREAM_BASE_URL() {
    return '/api/stream';
  },
  get ASSISTANT_BASE_URL() {
    return '/api/assistant';
  },
  
  // Mapbox Configuration
  MAPBOX_TOKEN: import.meta.env.VITE_MAPBOX_TOKEN || '',
  MAPBOX_STYLE: import.meta.env.VITE_MAPBOX_STYLE || 'mapbox://styles/mapbox/streets-v11',
  
  // Live-map GPU / columnar Arrow streaming path (green-field; ON by default, isolated from explore/fleet).
  // Set VITE_LIVE_MAP_GPU=false to fall back to the legacy SVG + JSON-WS path.
  LIVE_MAP_GPU: import.meta.env.VITE_LIVE_MAP_GPU !== 'false',

  // How deep the live-map columnar ring buffers keep per-boat history, in MINUTES of wall clock.
  //
  // This is what bounds how far back the live map can be paused or scrubbed without going to the
  // network: reads never leave the ring. The default covers LIVE_TIME_WINDOW_MAX_MINUTES (30) plus
  // slack, so every window the time-window dial offers is answerable from memory.
  //
  // A TUNING knob, not a feature flag. Cost is linear in boats x channels x minutes (~11 Float64
  // columns per boat), so roughly 1.8 MB per boat at the default — fine for a 6-boat fleet, worth
  // lowering on a constrained machine watching a large one.
  LIVE_MAP_RING_MINUTES: (() => {
    const raw = Number(import.meta.env.VITE_LIVE_MAP_RING_MINUTES);
    if (!Number.isFinite(raw) || raw <= 0) return 32;
    return Math.min(raw, 180); // hard ceiling: past this the ring is the wrong tool
  })(),

  // Pause / scrub the live map to a past time, rendering from retained (and, past the ring, fetched)
  // history instead of the live edge. ON by default; VITE_LIVE_MAP_SCRUB=false or ?livescrub=0
  // restores follow-only behaviour with an inert timeline.
  LIVE_MAP_SCRUB: (() => {
    try {
      const q = new URLSearchParams(window.location.search);
      if (q.get('livescrub') === '1') return true;
      if (q.get('livescrub') === '0') return false;
    } catch (_) { /* no window (SSR/build) */ }
    return import.meta.env.VITE_LIVE_MAP_SCRUB !== 'false';
  })(),

  // Explore/fleet map native-GL rendering (tracks = GL line layers, boat/maneuvers = Canvas2D overlays).
  // ON by default. Set VITE_MAP_GL_NATIVE=false to fall back to the legacy D3-SVG path, or use the URL
  // query ?glmap=0 to force off (and ?glmap=1 to force on) per-session without a rebuild.
  MAP_GL_NATIVE: (() => {
    try {
      const q = new URLSearchParams(window.location.search);
      if (q.get('glmap') === '1') return true;
      if (q.get('glmap') === '0') return false;
    } catch (_) { /* no window (SSR/build) */ }
    return import.meta.env.VITE_MAP_GL_NATIVE !== 'false';
  })(),

  // Animated windy.com-style wind flow drawn with the Mark Wind overlay (own canvas, capped at 30fps, never
  // repaints the map). ON by default; set VITE_MAP_WIND_ANIMATION=false to draw the static field only.
  MAP_WIND_ANIMATION: import.meta.env.VITE_MAP_WIND_ANIMATION !== 'false',

  // Development Configuration
  DEV_TOOLS: import.meta.env.VITE_DEV_TOOLS === 'true',
  DEBUG_MODE: import.meta.env.VITE_DEBUG_MODE === 'true',
  VERBOSE: import.meta.env.VITE_VERBOSE === 'true',
  IS_DEV: import.meta.env.DEV,
  IS_PROD: import.meta.env.PROD,
  
  // WebSocket Configuration
  // Enable when either VITE_ENABLE_WEBSOCKETS=true or ENABLE_WEBSOCKETS=true in .env
  ENABLE_WEBSOCKETS:
    import.meta.env.VITE_ENABLE_WEBSOCKETS === 'true' ||
    import.meta.env.ENABLE_WEBSOCKETS === 'true',

  // Media: when true, only med_res exists (e.g. upload bypass); player uses med_res only, no quality switching
  MEDIA_MED_RES_ONLY: import.meta.env.VITE_MEDIA_MED_RES_ONLY === 'true',

  // Dataset import: the server owns the pipeline after the XML upload, so the browser can close.
  // Default ON; set VITE_IMPORT_PIPELINE_SERVER_SIDE=false to fall back to the client-driven upload
  // loop for one release. Must be kept in step with IMPORT_PIPELINE_SERVER_SIDE on server_admin — the
  // kickoff endpoint returns 503 when the server side is disabled.
  IMPORT_PIPELINE_SERVER_SIDE: import.meta.env.VITE_IMPORT_PIPELINE_SERVER_SIDE !== 'false',

  // Environment
  NODE_ENV: import.meta.env.MODE || 'development',

  /**
   * POST /api/python/execute_script/ blocks until the script exits (server_python/app/main.py
   * SCRIPT_TIMEOUT_SECONDS = 3600). Client abort must not fire sooner or users see a false
   * "cancelled" alert while the script still runs.
   */
  EXECUTE_SCRIPT_FETCH_TIMEOUT_MS: 60 * 60 * 1000 + 2 * 60 * 1000,
};

// API Endpoint Helpers
// All endpoints use relative URLs (nginx handles routing)
export const apiEndpoints = {
  // Auth endpoints - JWT
  auth: {
    login: `${config.API_BASE_URL}/auth/login`,
    logout: `${config.API_BASE_URL}/auth/logout`,
    register: `${config.API_BASE_URL}/auth/register`,
    verify: `${config.API_BASE_URL}/auth/verify`,
    resetPassword: `${config.API_BASE_URL}/auth/reset-password`,
    forgotPassword: `${config.API_BASE_URL}/auth/forgot-password`,
    me: `${config.API_BASE_URL}/auth/user`,
    refresh: `${config.API_BASE_URL}/auth/refresh`,
    autoLogin: `${config.API_BASE_URL}/auth/refresh`, // Use refresh for auto-login
  },
  
  // Capitan assistant (server_assistant :8039)
  assistant: {
    base: '/api/assistant',
    health: '/api/assistant/health',
    ask: '/api/assistant/ask',
    askStream: '/api/assistant/ask/stream',
    askCancel: '/api/assistant/ask/cancel',
    conversations: '/api/assistant/conversations',
    feedback: '/api/assistant/feedback',
    memoryRemember: '/api/assistant/memory/remember',
    memoryCount: '/api/assistant/memory/count',
    adminPrompts: '/api/assistant/admin/prompts',
    adminMemoryFindings: '/api/assistant/admin/memory/findings',
    adminFeedback: '/api/assistant/admin/feedback',
    adminUsage: '/api/assistant/admin/usage',
  },

  // App endpoints (API_BASE_URL routes)
  app: {
    projects: `${config.API_BASE_URL}/projects`,
    datasets: `${config.API_BASE_URL}/datasets`,
    channels: `${config.API_BASE_URL}/datasets/channels`,
    comments: `${config.API_BASE_URL}/comments`,
    userEvents: `${config.API_BASE_URL}/user-events`,
    userEventsImportChatComments: `${config.API_BASE_URL}/user-events/import-chat-comments`,
    userEventsImportComments: `${config.API_BASE_URL}/user-events/import-comments`,
    events: `${config.API_BASE_URL}/events`,
    targets: `${config.API_BASE_URL}/targets`,
    baselines: `${config.API_BASE_URL}/baselines`,
    crewRoster: `${config.API_BASE_URL}/crew-roster`,
    crew: `${config.API_BASE_URL}/crew`,
    sources: `${config.API_BASE_URL}/sources`,
    pages: `${config.API_BASE_URL}/pages`,
    users: `${config.API_BASE_URL}/users`,
    userSharing: `${config.API_BASE_URL}/users/sharing`,
    usersPending: `${config.API_BASE_URL}/usersPending`,
    classes: `${config.API_BASE_URL}/classes`,
    data: `${config.API_BASE_URL}/data`,
    admin: {
      log_activity: `${config.API_BASE_URL}/admin/log_activity`,
      user_activity: `${config.API_BASE_URL}/admin/user_activity`,
      user_activity_summary: `${config.API_BASE_URL}/admin/user_activity/summary`,
      timezones: `${config.API_BASE_URL}/admin/timezones`,
    },
  },
  
  // Admin endpoints (ADMIN_BASE_URL routes)
  admin: {
    upload: `${config.ADMIN_BASE_URL}/upload`,
    // Current SailGP race course, proxied by the admin server (the feed sends no CORS headers)
    sailgp_race_course: `${config.ADMIN_BASE_URL}/upload/race-course/sailgp`,
    log: `${config.ADMIN_BASE_URL}/log`,
    projects: `${config.ADMIN_BASE_URL}/projects`,
    datasets: `${config.ADMIN_BASE_URL}/datasets`,
    events: `${config.ADMIN_BASE_URL}/events`,
    tokens: `${config.ADMIN_BASE_URL}/tokens`,
    targets: `${config.ADMIN_BASE_URL}/targets`,
    truncateDatasetsCascade: `${config.ADMIN_BASE_URL}/truncate-datasets-cascade`,
    mediaRemoveByDate: `${config.ADMIN_BASE_URL}/media/by-date`,
    script_jobs: `${config.ADMIN_BASE_URL}/script-jobs`,
    script_job: (jobId) => `${config.ADMIN_BASE_URL}/script-jobs/${jobId}`,
    script_job_cancel: (jobId) => `${config.ADMIN_BASE_URL}/script-jobs/${jobId}/cancel`,
    script_job_retry: (jobId) => `${config.ADMIN_BASE_URL}/script-jobs/${jobId}/retry`,
    script_jobs_clear: `${config.ADMIN_BASE_URL}/script-jobs/clear`,
    script_jobs_cancel_all: `${config.ADMIN_BASE_URL}/script-jobs/cancel-all`,
    script_jobs_queue_state: `${config.ADMIN_BASE_URL}/script-jobs/queue-state`,
    script_jobs_pause: `${config.ADMIN_BASE_URL}/script-jobs/pause`,
    script_jobs_resume: `${config.ADMIN_BASE_URL}/script-jobs/resume`,
    script_jobs_reconcile_stale: `${config.ADMIN_BASE_URL}/script-jobs/reconcile-stale`,
    import_runs: `${config.ADMIN_BASE_URL}/import-runs`,
    import_runs_active: `${config.ADMIN_BASE_URL}/import-runs/active`,
    import_run: (runId) => `${config.ADMIN_BASE_URL}/import-runs/${runId}`,
    import_run_cancel: (runId) => `${config.ADMIN_BASE_URL}/import-runs/${runId}/cancel`,
  },

  // Python endpoints
  python: {
    sse: `${config.PYTHON_BASE_URL}/sse`,
    fetch_data: `${config.PYTHON_BASE_URL}/fetch_data`,
    execute_script: `${config.PYTHON_BASE_URL}/execute_script/`, // Trailing slash required by FastAPI endpoint
    running_processes: `${config.PYTHON_BASE_URL}/scripts/running`,
    cancel_process: (processId) => `${config.PYTHON_BASE_URL}/scripts/cancel/${processId}`,
  },
  
  // File endpoints
  file: {
    channels: `${config.FILE_BASE_URL}/get-available-channels`, // Use get-available-channels to avoid route conflicts
    channelGroups: `${config.FILE_BASE_URL}/channel-groups`,
    channelValues: `${config.FILE_BASE_URL}/channel-values`,
    dates: `${config.FILE_BASE_URL}/dates`,
    sources: `${config.FILE_BASE_URL}/sources`,
    classes: `${config.FILE_BASE_URL}/classes`,
    influxdbAvailable: `${config.FILE_BASE_URL}/influxdb/available`,
  },
  
  // Media endpoints
  media: {
    video: '/api/media/video',
    sources: `${config.API_BASE_URL}/media/sources`,
    files: `${config.API_BASE_URL}/media`,
    removeByDate: `${config.ADMIN_BASE_URL}/media/by-date`,
  },
  
  // Stream endpoints (realtime data)
  stream: {
    config: `${config.STREAM_BASE_URL}/config`,
    status: `${config.STREAM_BASE_URL}/status`,
    sources: `${config.STREAM_BASE_URL}/sources`,
    /** Live mark-wind (arrows) queried directly from Influx mdss — LIVE map only. */
    markwindLive: `${config.STREAM_BASE_URL}/markwind/live`,
    /** Live BTN_* state queried directly from Influx (strm) — LIVE BUTTONS page only. */
    buttonsLive: `${config.STREAM_BASE_URL}/buttons/live`,
    sourceStatus: (sourceName) => `${config.STREAM_BASE_URL}/sources/${encodeURIComponent(sourceName)}/status`,
    sourceData: (sourceName) => `${config.STREAM_BASE_URL}/sources/${encodeURIComponent(sourceName)}/data`,
    /** Single Redis ZRANGEBYSCORE — full rows (all channels per timestamp) */
    sourceDataMerged: (sourceName) => `${config.STREAM_BASE_URL}/sources/${encodeURIComponent(sourceName)}/data/merged`,
    sourceChannels: (sourceName) => `${config.STREAM_BASE_URL}/sources/${encodeURIComponent(sourceName)}/channels`,
    websocket: `${config.STREAM_BASE_URL}/ws`,
    /** Green-field columnar (Arrow binary) live-map stream. */
    websocketArrow: `${config.STREAM_BASE_URL}/ws/arrow`,
    redisStatus: `${config.STREAM_BASE_URL}/redis/status`,
    monitoringStatus: `${config.STREAM_BASE_URL}/monitoring/status`,
    influxdbEnable: `${config.STREAM_BASE_URL}/influxdb/enable`,
    start: `${config.STREAM_BASE_URL}/start`,
    stop: `${config.STREAM_BASE_URL}/stop`,
    simulationStatus: `${config.STREAM_BASE_URL}/simulation/status`,
    simulationStart: `${config.STREAM_BASE_URL}/simulation/start`,
    simulationStop: `${config.STREAM_BASE_URL}/simulation/stop`,
    liveStatsEnable: `${config.STREAM_BASE_URL}/live-stats/enable`,
  },
};

// Validation function to check required environment variables
export function validateEnvironment() {
  // No longer require VITE_API_HOST since we always use relative URLs
  
  // Warn about optional but recommended variables
  if (!import.meta.env.VITE_MAPBOX_TOKEN || import.meta.env.VITE_MAPBOX_TOKEN.trim() === '') {
    warn('VITE_MAPBOX_TOKEN not set - Mapbox features will be disabled');
  }
  
  return true;
}

// Initialize validation in development
if (config.IS_DEV) {
  validateEnvironment();
}

export default config;
